import { isPlatformServer } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID, TransferState, makeStateKey } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { Client, Page, PagedResponse } from "@interfaces";
import { Observable, of } from "rxjs";
import { catchError, concatMap, map } from "rxjs/operators";
import { MetadataService } from "src/app/services/meta-service.service";
import { PagesService } from "../pages.service";
import { SubdomainService } from "../subdomain.service";

@Injectable({
  providedIn: "root",
})
export class PageResolverService  {
  constructor(
    private pages: PagesService,
    private subdomain: SubdomainService,
    private meta: MetadataService,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId,
    private router: Router
  ) {}

  public resolve(route: ActivatedRouteSnapshot): Observable<any> {
    let { slug } = route.params;
    
    if(route.firstChild) {
      slug = route.firstChild.params?.slug;
    }
    
    const SINGLE_KEY = makeStateKey<Page>("page-" + slug);

    if (slug === "page-not-found") {
      this.router.navigate(["page-not-found"]);
      return of(false);
    }

    if (this.transferState.hasKey(SINGLE_KEY)) {
      const page = this.transferState.get<Page>(SINGLE_KEY, null);
      this.transferState.remove(SINGLE_KEY);
      return of(page);
    } else {
      return this.subdomain.getClient().pipe(
        concatMap((client: Client) => {
          if(slug) {
            return this.pages.find({ slug, employer: client._id });
          } else {
            return this.pages.find({ defaultPage: true, employer: client._id });
          }
        }),
        map((result: PagedResponse<Page>) => {
          const page = result.data[0];
          if (!page) throw new Error("Page not found.");
          if (isPlatformServer(this.platformId)) {
            this.meta.setupPageMetaTags(page);
            this.transferState.set(SINGLE_KEY, page);
          }
          return page;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 500) {
            this.router.navigate(["page-not-found"]);
          } else {
            this.router.navigate(["error"]);
          }
          return of(false);
        })
      );
    }
  }
}
